import { Collection, Database } from '@nozbe/watermelondb';

import Contact from '../../Contact';
import Entry from '../../Entry';
import EntryProcedure from '../../EntryProcedure';
import Horse from '../../Horse';
import { EntryModel } from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { ContactModel } from '../../../../types/Contacts';
import { HorseModel } from '../../../../types/Horses';
import {
    InvoiceModel,
    InvoicePayload,
    ParsedInvoiceProduct,
    PreparedDataOnInvoiceAdd,
} from '../../../../types/Invoice';
import { Identificator } from '../../../../types/Identificator';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { generateReference } from '../../../../utils/invoicing';
import InvoiceInvoicingProduct from '../../../models/InvoiceInvoicingProduct';
import { toPreparedDataItems } from './common';

type InvoiceTotals = {
    subtotalToCheck: string;
    totalToCheck: string;
};

type InvoiceAddBumpFetch = {
    entryProceduresElements: EntryProcedureModel[];
    horseElements: HorseModel[];
};

type InvoiceAddDataToBump = {
    entryProcedures: string[];
    horses: string[];
};

const prepareCreateInvoice = ({
    payload,
    userId,
    organisationID,
    totals,
    collection,
}: {
    payload: InvoicePayload;
    userId: string;
    organisationID: string;
    totals: InvoiceTotals;
    collection: Collection<InvoiceModel>;
}) => {
    const { subtotalToCheck, totalToCheck } = totals;

    return collection.prepareCreate((invoice) => {
        invoice.number = payload.number || '';
        invoice.reference = generateReference();
        invoice.applyTaxes = payload.applyTaxes;
        invoice.status = payload.status;
        invoice.deepLink = payload.deepLink || '';
        invoice.publicUrl = payload.publicUrl || '';
        invoice.subTotal = !payload.applyTaxes
            ? subtotalToCheck
            : payload.subTotal || '';
        invoice.totalTax = payload.totalTax || '';
        invoice.total = payload.applyTaxes ? totalToCheck : payload.total || '';
        invoice.totalDiscount = payload.totalDiscount || '';
        invoice.sentToContact = payload.sentToContact || false;
        invoice.authorisedTime = payload.authorisedTime;
        invoice.invoiceDate = payload.invoiceDate || '';
        invoice.dueDateTime = payload.dueDateTime;
        invoice.sentToContactTime = payload.sentToContactTime || '';
        invoice.paymentDateTime = payload.paymentDateTime || '';
        invoice.contactId = payload.contactId;
        invoice.externalId = payload.externalId || '';
        invoice.attachReports = payload.attachReports;
        invoice.userId = userId;
        invoice.organisationId = organisationID;
        invoice.amountCheck = subtotalToCheck;
        invoice.footerText = payload.footerText || '';
        invoice.provider = payload.provider;
        invoice.taxRate = payload.taxRate;
        invoice.productsTaxRate = payload.productsTaxRate;
    });
};

const fetchInvoiceEntryElements = async ({
    entryIds,
    entryService,
}: {
    entryIds: Identificator[];
    entryService: Entry;
}) => {
    return Promise.all(
        entryIds.map((entryId) => entryService.getByID(entryId.id)),
    );
};

const prepareInvoiceEntryUpdates = ({
    invoice,
    entryIds,
    entryElements,
}: {
    invoice: InvoiceModel;
    entryIds: Identificator[];
    entryElements: EntryModel[];
}) => {
    const entriesToUpdate = entryIds.map((_, index) => {
        const entry = entryElements[index];

        return entry.prepareUpdate((entry) => {
            entry.invoiceId = invoice.id;
        });
    });

    return { entriesToUpdate };
};

const appendPreparedInvoiceEntries = (
    preparedData: PreparedDataOnInvoiceAdd[],
    entriesData: ReturnType<typeof prepareInvoiceEntryUpdates>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            entriesData.entriesToUpdate,
            'entries',
            'update',
        ),
    ];
};

const prepareInvoiceProducts = ({
    invoice,
    products,
    userId,
    organisationID,
    database,
}: {
    invoice: InvoiceModel;
    products: ParsedInvoiceProduct[];
    userId: string;
    organisationID: string;
    database: Database;
}) => {
    const invoiceProductsCollection =
        database.collections.get<InvoiceInvoicingProduct>(
            'invoice_invoicing_products',
        );
    const invoiceProductsToAdd = products.map((product) =>
        invoiceProductsCollection.prepareCreate(
            (invoiceInvoicingProduct: InvoiceInvoicingProduct) => {
                invoiceInvoicingProduct.invoiceId = invoice.id;
                invoiceInvoicingProduct.invoicingProductId =
                    product.invoicingProductId;
                invoiceInvoicingProduct.quantity = product.quantity;
                invoiceInvoicingProduct.userId = userId;
                invoiceInvoicingProduct.organisationId = organisationID;
                invoiceInvoicingProduct.price = product.price;
                invoiceInvoicingProduct.name = product.name;
            },
        ),
    );

    return { invoiceProductsToAdd };
};

const appendPreparedInvoiceProducts = (
    preparedData: PreparedDataOnInvoiceAdd[],
    productsData: ReturnType<typeof prepareInvoiceProducts>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            productsData.invoiceProductsToAdd,
            'invoice_invoicing_products',
            'add',
        ),
    ];
};

const fetchContactElement = async ({
    contactId,
    contactService,
}: {
    contactId: string;
    contactService: Contact;
}) => {
    return contactService.getByID(contactId);
};

const prepareInvoiceContactUpdate = ({
    contactElement,
}: {
    contactElement: ContactModel;
}) => {
    const contactToUpdate = contactElement.prepareUpdate((contact) => {
        contact.billable = true;
    });

    return { contactElement, contactToUpdate };
};

const appendPreparedContact = (
    preparedData: PreparedDataOnInvoiceAdd[],
    contactData: ReturnType<typeof prepareInvoiceContactUpdate>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            [contactData.contactToUpdate],
            'contacts',
            'update',
        ),
    ];
};

const fetchBumpElements = async ({
    dataToBump,
    entryProcedureService,
    horseService,
}: {
    dataToBump: InvoiceAddDataToBump;
    entryProcedureService: EntryProcedure;
    horseService: Horse;
}): Promise<InvoiceAddBumpFetch> => {
    const [entryProceduresElements, horseElements] = await Promise.all([
        Promise.all(
            dataToBump.entryProcedures.map((id) =>
                entryProcedureService.getByID(id),
            ),
        ),
        Promise.all(dataToBump.horses.map((id) => horseService.getByID(id))),
    ]);

    return {
        entryProceduresElements,
        horseElements,
    };
};

const prepareBumpUpdates = (bumpFetch: InvoiceAddBumpFetch) => {
    const entryProceduresToBump = bumpFetch.entryProceduresElements.map(
        (procedureElement) =>
            procedureElement.prepareUpdate((procedure) => {
                procedure.name = procedureElement.name;
            }),
    );
    const horsesToBump = bumpFetch.horseElements.map((horseElement) =>
        horseElement.prepareUpdate((horse) => {
            horse.name = horseElement.name;
        }),
    );

    return {
        entryProceduresToBump,
        horsesToBump,
    };
};

const appendPreparedBumpEntryProcedures = (
    preparedData: PreparedDataOnInvoiceAdd[],
    bumpData: ReturnType<typeof prepareBumpUpdates>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            bumpData.entryProceduresToBump,
            'entry_procedures',
            'update',
        ),
    ];
};

const appendPreparedBumpHorses = (
    preparedData: PreparedDataOnInvoiceAdd[],
    bumpData: ReturnType<typeof prepareBumpUpdates>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(bumpData.horsesToBump, 'horses', 'update'),
    ];
};

const logDBActions = ({
    invoice,
    entriesData,
    contactData,
    preparedBumpData,
    logDBAction,
    tableName,
}: {
    invoice: InvoiceModel;
    entriesData: ReturnType<typeof prepareInvoiceEntryUpdates>;
    contactData?: ReturnType<typeof prepareInvoiceContactUpdate>;
    preparedBumpData?: ReturnType<typeof prepareBumpUpdates>;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Created invoice',
        modelName: tableName,
        payload: invoice,
    });

    if (contactData) {
        logDBAction({
            message: 'Update contact field billable',
            modelName: tableName,
            payload: contactData.contactElement,
        });
    }

    logDBAction({
        message: 'Create invoice - update entries',
        modelName: tableName,
        payload: entriesData.entriesToUpdate,
    });

    logDBAction({
        message: 'Create invoice - bump entry procedures',
        modelName: tableName,
        payload: preparedBumpData?.entryProceduresToBump ?? [],
    });

    logDBAction({
        message: 'Create invoice - bump horses',
        modelName: tableName,
        payload: preparedBumpData?.horsesToBump ?? [],
    });
};

export type { InvoiceAddDataToBump };

export {
    appendPreparedBumpEntryProcedures,
    appendPreparedBumpHorses,
    appendPreparedContact,
    appendPreparedInvoiceEntries,
    appendPreparedInvoiceProducts,
    prepareBumpUpdates,
    prepareInvoiceContactUpdate,
    prepareInvoiceEntryUpdates,
    fetchBumpElements,
    fetchContactElement,
    fetchInvoiceEntryElements,
    logDBActions,
    prepareCreateInvoice,
    prepareInvoiceProducts,
};
